import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetShowTasks, apiCreateShowTask, apiUpdateShowTask, apiDeleteShowTask, apiGetTeam } from '../api';
import type { ShowTask, ShowTaskStatus, TeamMemberDTO } from '../api';
import { showConfirm } from './ConfirmDialog';
import { useWriteGate } from '../lib/writeGate';
import { withCardIcon } from '../lib/uiIcons';

const STATUSES: ShowTaskStatus[] = ['todo', 'in_progress', 'done'];

/** A single editable task row. */
function TaskRow({
  task,
  members,
  canWrite,
  onUpdate,
  onDelete,
}: {
  task: ShowTask;
  members: TeamMemberDTO[];
  canWrite: boolean;
  onUpdate: (id: number, patch: Partial<ShowTask>) => void;
  onDelete: (id: number) => void;
}) {
  const { t } = useTranslation();
  const memberName = (id: string) => {
    if (!id) return t('showTasks.unassigned');
    const m = members.find((x) => String(x.id) === String(id));
    return m ? m.nickname || m.name : t('showTasks.unassigned');
  };
  return (
    <div className="rc-card col" data-task-row>
      {/* Row 1 — the task itself */}
      <div className="rc-card-fields">
        <div className="form-field" style={{ flex: '1 1 240px', minWidth: 0 }}>
          <label>{t('showTasks.colTitle')}</label>
          <input
            className="form-input"
            value={task.title}
            disabled={!canWrite}
            onChange={(e) => onUpdate(task.id, { title: e.target.value })}
            placeholder={t('showTasks.titlePh')}
          />
        </div>
      </div>
      {/* Row 2 — assignee / due / status (+ delete, or the assignee note when frozen) */}
      <div className="rc-card-fields" style={{ alignItems: 'flex-end' }}>
        <div className="form-field" style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label>{t('showTasks.colAssignee')}</label>
          <select
            className="form-input"
            value={task.assigned_to || ''}
            disabled={!canWrite}
            onChange={(e) => onUpdate(task.id, { assigned_to: e.target.value })}
          >
            <option value="">{t('showTasks.unassigned')}</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.nickname || m.name}</option>
            ))}
          </select>
        </div>
        <div className="form-field" style={{ flex: '1 1 140px', minWidth: 0 }}>
          <label>{t('showTasks.colDue')}</label>
          <input
            type="date"
            className="form-input"
            value={task.due_date || ''}
            disabled={!canWrite}
            onChange={(e) => onUpdate(task.id, { due_date: e.target.value })}
          />
        </div>
        <div className="form-field" style={{ flex: '1 1 150px', minWidth: 0 }}>
          <label>{t('showTasks.colStatus')}</label>
          <select
            className="form-input"
            value={task.status}
            disabled={!canWrite}
            onChange={(e) => onUpdate(task.id, { status: e.target.value as ShowTaskStatus })}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{t(`showTasks.status.${s}`)}</option>
            ))}
          </select>
        </div>
        {canWrite && (
          <button
            className="btn btn-danger btn-sm"
            title={t('showTasks.delete')}
            onClick={() => void onDelete(task.id)}
            style={{ flex: '0 0 auto', fontSize: 'var(--font-xs)', padding: '4px 8px' }}
          >
            ✕
          </button>
        )}
        {!canWrite && (
          <span className="text-dim" style={{ flex: '0 0 auto', color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>
            {memberName(task.assigned_to)}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * ShowTasksSection — per-show to-do list assigned to team members. Rendered
 * inside the show's Day tab (integrated into the show page, no separate menu).
 *
 * Reads for viewer+ on the show's band; only editor+ can add/edit/delete
 * (enforced server-side too).
 */
export default function ShowTasksSection({
  showId,
  bandId,
  canWrite,
}: {
  showId: string | null;
  bandId: string;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  // Capability-aware write gate (lot A1): the `canWrite` prop is the caller's
  // coarse opinion, the gate is the rule — a global read-only account (even with
  // a band editor role) must not emit a task write, the server 403s them.
  const gate = useWriteGate(bandId);
  const canEdit = canWrite && gate.canWrite;
  const [tasks, setTasks] = useState<ShowTask[]>([]);
  const [members, setMembers] = useState<TeamMemberDTO[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!showId) return;
    try {
      const list = await apiGetShowTasks(showId);
      setTasks(list);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, [showId]);

  useEffect(() => {
    let cancelled = false;
    if (bandId) {
      apiGetTeam({ band: bandId })
        .then((m) => { if (!cancelled) setMembers(m); })
        .catch(() => { if (!cancelled) setMembers([]); });
    }
    return () => { cancelled = true; };
  }, [bandId]);

  useEffect(() => {
    setTasks([]);
    setLoaded(false);
    void load();
  }, [load]);

  const add = async () => {
    if (!showId || !newTitle.trim() || busy || !canEdit) return;
    setBusy(true);
    try {
      const created = await apiCreateShowTask(showId, { title: newTitle.trim() });
      setTasks((prev) => [...prev, created]);
      setNewTitle('');
    } catch {
      // create failed — keep the draft so the user can retry
    } finally {
      setBusy(false);
    }
  };

  const update = async (id: number, patch: Partial<ShowTask>) => {
    if (!showId || !canEdit) return;
    // Optimistic update; server call reconciles.
    setTasks((prev) => prev.map((tk) => (tk.id === id ? { ...tk, ...patch } : tk)));
    try {
      const updated = await apiUpdateShowTask(showId, id, patch);
      setTasks((prev) => prev.map((tk) => (tk.id === id ? updated : tk)));
    } catch {
      // keep optimistic state so edits are not lost
    }
  };

  const remove = async (id: number) => {
    if (!showId || !canEdit) return;
    const ok = await showConfirm(t('showTasks.deleteConfirm'), {
      title: t('showTasks.delete'),
      confirmLabel: t('showTasks.delete'),
      danger: true,
    });
    if (!ok) return;
    try {
      await apiDeleteShowTask(showId, id);
      setTasks((prev) => prev.filter((tk) => tk.id !== id));
    } catch {
      // delete failed — row stays
    }
  };

  return (
    <div className="card" data-daytab="tasks" style={{ maxWidth: 1000, marginBottom: 20 }}>
      <div className="rc-card-head">
        <div>
          <h3 className="rc-header-title">{withCardIcon('show-day:tasks', t('showTasks.title'))}</h3>
          <p className="rc-header-sub">{t('showTasks.hint')}</p>
        </div>
        {canEdit && (
          <div className="rc-header-actions">
            <input
              className="form-input"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
              placeholder={t('showTasks.newTaskPh')}
              style={{ flex: '1 1 220px', minWidth: 200 }}
            />
            <button className="btn btn-primary" onClick={() => void add()} disabled={!newTitle.trim() || busy}>
              {busy ? t('showTasks.adding') : t('showTasks.add')}
            </button>
          </div>
        )}
      </div>

      {!loaded ? (
        <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('showTasks.loading')}</p>
      ) : tasks.length === 0 ? (
        <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('showTasks.empty')}</p>
      ) : (
        <div className="rc-list" data-task-list>
          {tasks.map((tk) => (
            <TaskRow
              key={tk.id}
              task={tk}
              members={members}
              canWrite={canEdit}
              onUpdate={update}
              onDelete={(id) => void remove(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
